import { useQuery } from '@tanstack/react-query';
import { CheckIcon, ChevronsUpDownIcon } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DomainValue } from '@/common/dropdowns';
import {
	DeletedStatusLabel,
	deletedOptionClassName,
	deletedTriggerClassName,
} from '@/components/soft-delete/StatusFilterSelect';
import type { DROPDOWN_NAMES } from '@/hooks/use-dropdowns';
import { cn } from '@/lib/utils';
import { getDropdownsOptions } from '@/queries/appconfig/get-dropdowns';

import { Button } from '../ui/button';
import { Command, CommandGroup, CommandItem, CommandList, CommandSeparator } from '../ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';

export type ConfigFilterSelectProps = {
	dropdown: DROPDOWN_NAMES;
	label: string;
	pluralLabel?: string;
	value?: string[] | null;
	onChange?: (value: string[]) => void;
	placeholder?: string;
	disabled?: boolean;
	isMulti?: boolean;
	defaultValue?: string[];
	popoverButtonProps?: React.ComponentProps<typeof Button>;
	/**
	 * Adds "Deleted" as the last option, below a separator (GH #331). It is exclusive: picking it
	 * hides the selected statuses, and picking a status again leaves it.
	 */
	deletedOption?: { selected: boolean; onSelectedChange: (selected: boolean) => void };
};

type Option = {
	value: string;
	label: string;
	id: number;
};
type GroupedOption = {
	group: string;
	options: Option[];
};

type ConfigGroupedOption = {
	options: GroupedOption[];
	isGroup: true;
};

type ConfigUngroupedOption = {
	options: Option[];
	isGroup: false;
};

export function ConfigFilterSelect({
	dropdown,
	value,
	onChange,
	placeholder,
	disabled,
	label,
	pluralLabel,
	popoverButtonProps = {},
	deletedOption,
}: ConfigFilterSelectProps) {
	const { t } = useTranslation();
	const effectivePlaceholder = placeholder ?? t('damages.filter.defaultPlaceholder');
	const { data: domainValuesRes, isLoading } = useQuery(getDropdownsOptions());
	const domainValues = domainValuesRes?.domainValues || [];

	const { options, isGroup } = useMemo((): ConfigUngroupedOption | ConfigGroupedOption => {
		const rootDomain = domainValues.find((d) => d.value === 'ROOT');
		if (!rootDomain) {
			return {
				options: [],
				isGroup: false,
			};
		}
		const filtered = domainValues.filter((d) => d.group === dropdown);

		const parents = filtered.filter((d) => rootDomain.id === d.parentId);
		const children = filtered.filter((d) => rootDomain.id !== d.parentId);

		const map: Record<number, DomainValue[]> = {};

		for (const child of children) {
			if (!child.parentId) {
				continue;
			}
			if (!map[child.parentId]) {
				map[child.parentId] = [];
			}
			map[child.parentId].push(child);
		}

		const parentMap = parents.map((parent) => ({
			group: parent.label,
			options: (map[parent.id] || []).map((c) => ({
				id: c.id,
				label: c.label,
				value: c.value,
			})),
		}));

		// If all parents have no children, we can treat it as ungrouped
		if (parentMap.every((g) => g.options.length === 0)) {
			return {
				options: filtered.map((d) => ({
					id: d.id,
					label: d.label,
					value: d.value,
				})),
				isGroup: false,
			};
		}
		// Otherwise, we treat it as grouped, even if some parents have no children
		return {
			options: parentMap,
			isGroup: true,
		};
	}, [domainValues, dropdown]);

	const deletedSelected = !!deletedOption?.selected;
	const arrValue = useMemo(() => {
		// While "Deleted" is picked, no status is applied
		if (!value || value.length === 0 || deletedSelected) {
			return [];
		}
		return value;
	}, [value, deletedSelected]);

	const handleChange = (val: string) => {
		if (!onChange) {
			return;
		}
		if (deletedSelected) {
			// Back from "Deleted" to the statuses, starting with the one just picked
			deletedOption?.onSelectedChange(false);
			onChange([val]);
			return;
		}
		// Toggle selection
		const newValue = arrValue.includes(val)
			? arrValue.filter((v) => v !== val)
			: [...arrValue, val];
		onChange(newValue);
	};

	return (
		<Popover>
			<PopoverTrigger asChild>
				{/* A constant width, not sized to the label: otherwise picking "Deleted" (short) or "4 Statuses
				    selected" (long) moves the button between lines of a wrapping filter bar */}
				<Button
					className={cn(
						'bg-white border border-border text-sm h-8 w-full sm:w-48 justify-between font-normal',
						deletedSelected && deletedTriggerClassName,
					)}
					role="combobox"
					aria-label={label}
					variant="outline"
					disabled={disabled}
					{...popoverButtonProps}
				>
					<span className="truncate">
						{deletedSelected
							? t('common.deleted.status')
							: arrValue.length > 0
							? t('damages.filter.selectedCount', {
									count: arrValue.length,
									noun: arrValue.length > 1 ? pluralLabel || label : label,
								})
							: effectivePlaceholder}
					</span>
					<ChevronsUpDownIcon className="ml-2 size-4 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			{/* At least as wide as the button, but wide enough for the longest option: the button sizes to its
			    label (e.g. "Deleted"), and a popover pinned to its width would cut the option names off */}
			<PopoverContent className="p-0 w-auto min-w-(--radix-popover-trigger-width) max-w-(--radix-popover-content-available-width)">
				<Command>
					<CommandList>
						{isLoading && (
							<div className="p-2 text-sm text-muted-foreground text-center">{t('damages.select.loading')}</div>
						)}
						{options.length === 0 && !isLoading && (
							<div className="p-2 text-sm text-muted-foreground text-center">
								{t('damages.select.noOptions')}
							</div>
						)}
						{isGroup &&
							options.map((group) => (
								<CommandGroup key={group.group} heading={group.group}>
									{group.options.map((opt) => (
										<CommandItem
											key={String(opt.id)}
											onSelect={handleChange}
											value={String(opt.id)}
										>
											<CheckIcon
												className={cn(
													'mr-2 size-4',
													arrValue.includes(String(opt.id)) ? 'opacity-100' : 'opacity-0'
												)}
											/>
											{opt.label}
										</CommandItem>
									))}
								</CommandGroup>
							))}
						{!isGroup &&
							options.map((opt) => (
								<CommandItem key={String(opt.id)} onSelect={handleChange} value={String(opt.id)}>
									<CheckIcon
										className={cn(
											'mr-2 size-4',
											arrValue.includes(String(opt.id)) ? 'opacity-100' : 'opacity-0'
										)}
									/>
									{opt.label}
								</CommandItem>
							))}
						{deletedOption && (
							<>
								<CommandSeparator />
								<CommandItem
									value="__deleted__"
									className={deletedOptionClassName}
									onSelect={() => deletedOption.onSelectedChange(!deletedSelected)}
								>
									<CheckIcon className={cn('mr-2 size-4', deletedSelected ? 'opacity-100' : 'opacity-0')} />
									<DeletedStatusLabel />
								</CommandItem>
							</>
						)}
					</CommandList>
				</Command>
			</PopoverContent>
		</Popover>
	);
}
